import type { NextRouter } from 'next/router';

// A link to the page you are already on must not add a history entry. The island's "Action needed" pill
// pushed `/tasks?tab=waiting` on every press, so a reader who used it from the Tasks screen built a back
// stack of identical entries — the back button seemed to do nothing, and the history menu was a column of
// the same page (reported 2026-10-24). Same target → `replace`, which still re-runs the route (a fresh
// `?tab=` resets a user-picked tab) without growing the stack.

/** Whether `as` (a browser path, with the mount prefix) is the page the window is showing now. */
export function isCurrentLocation(as: string, location: Pick<Location, 'pathname' | 'search'>): boolean {
  const target = new URL(as, 'https://x.invalid');
  const here = new URL(`${location.pathname}${location.search}`, 'https://x.invalid');
  return target.pathname === here.pathname && sameQuery(target.searchParams, here.searchParams);
}

/** The chat's own view state, which `useChatUrlSync` stamps onto whatever page is showing (by `replaceState`). */
const VIEW_STATE_PARAMS = new Set(['chat', 'conversation']);

function sameQuery(a: URLSearchParams, b: URLSearchParams): boolean {
  const sorted = (p: URLSearchParams) =>
    [...p.entries()].filter(([k]) => !VIEW_STATE_PARAMS.has(k)).map(([k, v]) => `${k}=${v}`).sort().join('&');
  return sorted(a) === sorted(b);
}

/** `router.push`, or `router.replace` when the target is the current page. */
export function pushUnlessCurrent(router: NextRouter, href: Parameters<NextRouter['push']>[0], as: string): Promise<boolean> {
  const current = typeof window !== 'undefined' && isCurrentLocation(as, window.location);
  return current ? router.replace(href, as) : router.push(href, as);
}
